'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import ServiceComparisonMatrix from './ServiceComparisonMatrix';
import CapabilityAssessment from './CapabilityAssessment';
import ImplementationTimeline from './ImplementationTimeline';
import CTASection from './CTASection';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  alt: string;
  features: {label: string;included: boolean;}[];
  benefits: string[];
  icon: string;
  detailsLink: string;
  pricing: {
    model: string;
    startingAt?: string;
  };
}

export default function ServicesOverviewInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All Services');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
  {
    id: 'managed-it',
    title: 'Managed IT Services',
    category: 'Infrastructure',
    description: 'Comprehensive IT infrastructure management with proactive monitoring, maintenance, and support to keep your systems running smoothly 24/7.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b38f162-1767683091191.png",
    alt: 'Modern server room with blue LED lighting showing rows of network equipment and data center infrastructure',
    features: [
    { label: '24/7 System Monitoring', included: true },
    { label: 'Proactive Maintenance', included: true },
    { label: 'Help Desk Support', included: true },
    { label: 'Network Management', included: true },
    { label: 'Hardware Procurement', included: true }],

    benefits: [
    'Reduce downtime by up to 95%',
    'Lower IT costs by 30-40%',
    'Predictable monthly expenses',
    'Access to enterprise-level expertise'],

    icon: 'ServerIcon',
    detailsLink: '/services/managed-it',
    pricing: {
      model: 'Per User/Month',
      startingAt: '$99'
    }
  },
  {
    id: 'cybersecurity',
    title: 'Cybersecurity Solutions',
    category: 'Security',
    description: 'Advanced threat protection, vulnerability assessments, and security compliance management to safeguard your business from cyber threats.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d1b5f69-1764669605749.png",
    alt: 'Digital security concept showing glowing blue padlock hologram floating above computer keyboard in dark environment',
    features: [
    { label: 'Threat Detection & Response', included: true },
    { label: 'Firewall Management', included: true },
    { label: 'Security Audits', included: true },
    { label: 'Compliance Management', included: true },
    { label: 'Employee Training', included: true }],

    benefits: [
    'Protect against ransomware attacks',
    'Ensure regulatory compliance',
    'Reduce security incidents by 80%',
    'Build customer trust'],

    icon: 'ShieldCheckIcon',
    detailsLink: '/services/cybersecurity',
    pricing: {
      model: 'Custom Quote',
      startingAt: '$499'
    }
  },
  {
    id: 'cloud-solutions',
    title: 'Cloud Solutions',
    category: 'Cloud',
    description: 'Seamless cloud migration, management, and optimization services for AWS, Azure, and Google Cloud platforms.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_128abc72d-1767860465521.png",
    alt: 'Abstract cloud computing visualization with interconnected blue and white digital nodes representing data flow',
    features: [
    { label: 'Cloud Migration Planning', included: true },
    { label: 'Multi-Cloud Management', included: true },
    { label: 'Cost Optimization', included: true },
    { label: 'Backup & Disaster Recovery', included: true },
    { label: 'Performance Monitoring', included: true }],

    benefits: [
    'Scale resources on demand',
    'Reduce infrastructure costs by 40%',
    'Improve collaboration',
    'Access data from anywhere'],

    icon: 'CloudIcon',
    detailsLink: '/services/cloud-solutions',
    pricing: {
      model: 'Usage-Based',
      startingAt: '$299'
    }
  },
  {
    id: 'emergency-support',
    title: '24/7 Emergency Support',
    category: 'Support',
    description: 'Round-the-clock emergency IT support with guaranteed response times to minimize business disruption.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1297793c4-1764661964989.png",
    alt: 'IT support specialist wearing headset working at computer workstation with multiple monitors in modern office',
    features: [
    { label: 'Immediate Response', included: true },
    { label: 'Remote & On-Site Support', included: true },
    { label: 'Priority Escalation', included: true },
    { label: 'Disaster Recovery', included: true },
    { label: 'Business Continuity', included: true }],

    benefits: [
    'Average response time under 15 minutes',
    'Minimize revenue loss',
    'Expert problem resolution',
    'Peace of mind 24/7'],

    icon: 'BoltIcon',
    detailsLink: '/services/emergency-support',
    pricing: {
      model: 'Retainer-Based',
      startingAt: '$199'
    }
  },
  {
    id: 'digital-transformation',
    title: 'Digital Transformation',
    category: 'Consulting',
    description: 'Strategic consulting to modernize your business processes, implement new technologies, and drive innovation.',
    image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3",
    alt: 'Business analytics dashboard on laptop screen showing colorful graphs, charts and performance metrics',
    features: [
    { label: 'Technology Assessment', included: true },
    { label: 'Process Optimization', included: true },
    { label: 'Change Management', included: true },
    { label: 'ROI Analysis', included: true },
    { label: 'Implementation Support', included: true }],

    benefits: [
    'Increase operational efficiency',
    'Improve customer experience',
    'Enable data-driven decisions',
    'Stay competitive'],

    icon: 'SparklesIcon',
    detailsLink: '/services/digital-transformation',
    pricing: {
      model: 'Project-Based',
      startingAt: '$5,000'
    }
  },
  {
    id: 'compliance-management',
    title: 'Compliance Management',
    category: 'Security',
    description: 'Comprehensive compliance solutions for HIPAA, PCI-DSS, SOC 2, and other regulatory requirements.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f1bc7bd9-1766749602496.png",
    alt: 'Professional reviewing compliance documents with digital tablet showing security checklist and certification badges',
    features: [
    { label: 'Compliance Audits', included: true },
    { label: 'Policy Development', included: true },
    { label: 'Risk Assessment', included: true },
    { label: 'Documentation Support', included: true },
    { label: 'Ongoing Monitoring', included: true }],

    benefits: [
    'Avoid costly penalties',
    'Build customer trust',
    'Streamline audit processes',
    'Maintain certifications'],

    icon: 'DocumentCheckIcon',
    detailsLink: '/services/compliance',
    pricing: {
      model: 'Annual Contract',
      startingAt: '$799'
    }
  },
  {
    id: 'low-current-systems',
    title: 'Low Current Systems',
    category: 'Infrastructure',
    description: 'Professional installation and maintenance of structured cabling, networking infrastructure, audio-visual systems, and building automation solutions.',
    image: "https://images.unsplash.com/photo-1416504919550-a469cfedc46f",
    alt: 'Organized network cables and structured cabling system in server room with color-coded wiring',
    features: [
    { label: 'Structured Cabling', included: true },
    { label: 'Network Infrastructure', included: true },
    { label: 'Audio-Visual Systems', included: true },
    { label: 'Building Automation', included: true },
    { label: 'Maintenance & Support', included: true }],

    benefits: [
    'Reliable network performance',
    'Future-proof infrastructure',
    'Reduced installation costs',
    'Professional cable management'],

    icon: 'CircuitBoardIcon',
    detailsLink: '/services/low-current-systems',
    pricing: {
      model: 'Project-Based',
      startingAt: '$2,500'
    }
  },
  {
    id: 'biometric-security',
    title: 'Facial & Biometric Security',
    category: 'Security',
    description: 'Advanced biometric access control systems including facial recognition, fingerprint scanners, and integrated security solutions for enhanced facility protection.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3ac08e2-1764670834596.png",
    alt: 'Modern facial recognition system with digital interface showing biometric authentication technology',
    features: [
    { label: 'Facial Recognition Systems', included: true },
    { label: 'Fingerprint Scanners', included: true },
    { label: 'Access Control Integration', included: true },
    { label: 'Time & Attendance Tracking', included: true },
    { label: 'Multi-Factor Authentication', included: true }],

    benefits: [
    'Enhanced security and access control',
    'Eliminate unauthorized access',
    'Automated attendance tracking',
    'Seamless integration with existing systems'],

    icon: 'FingerPrintIcon',
    detailsLink: '/services/biometric-security',
    pricing: {
      model: 'Custom Quote',
      startingAt: '$3,500'
    }
  }];


  const categories = ['All Services', 'Infrastructure', 'Security', 'Cloud', 'Support', 'Consulting'];

  const filteredServices = services.filter((service) => {
    const matchesCategory = selectedCategory === 'All Services' || service.category === selectedCategory;
    const matchesSearch = service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const comparisonServices = [
  {
    name: 'Managed IT',
    infrastructure: true,
    security: true,
    cloud: true,
    support247: true,
    compliance: false,
    consulting: false
  },
  {
    name: 'Cybersecurity',
    infrastructure: false,
    security: true,
    cloud: false,
    support247: true,
    compliance: true,
    consulting: false
  },
  {
    name: 'Cloud Solutions',
    infrastructure: true,
    security: true,
    cloud: true,
    support247: true,
    compliance: false,
    consulting: true
  },
  {
    name: 'Emergency Support',
    infrastructure: true,
    security: false,
    cloud: false,
    support247: true,
    compliance: false,
    consulting: false
  }];


  const assessmentQuestions = [
  {
    id: 'q1',
    question: 'What is your primary IT challenge?',
    options: [
    'System downtime and reliability issues',
    'Security threats and data protection',
    'Outdated infrastructure and technology',
    'Lack of in-house IT expertise']

  },
  {
    id: 'q2',
    question: 'How many employees does your organization have?',
    options: [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '200+ employees']

  },
  {
    id: 'q3',
    question: 'What is your current IT budget allocation?',
    options: [
    'Less than $5,000/month',
    '$5,000-$15,000/month',
    '$15,000-$50,000/month',
    'More than $50,000/month']

  }];


  const timelinePhases = [
  {
    phase: 'Discovery & Assessment',
    duration: '1-2 Weeks',
    activities: [
    'Initial consultation and needs analysis',
    'Current infrastructure audit',
    'Risk assessment and gap analysis',
    'Custom solution design'],

    icon: 'MagnifyingGlassIcon'
  },
  {
    phase: 'Planning & Design',
    duration: '2-3 Weeks',
    activities: [
    'Detailed implementation roadmap',
    'Resource allocation and scheduling',
    'Stakeholder alignment meetings',
    'Documentation preparation'],

    icon: 'DocumentTextIcon'
  },
  {
    phase: 'Implementation',
    duration: '4-8 Weeks',
    activities: [
    'System deployment and configuration',
    'Data migration and integration',
    'Testing and quality assurance',
    'User training sessions'],

    icon: 'WrenchScrewdriverIcon'
  },
  {
    phase: 'Optimization & Support',
    duration: 'Ongoing',
    activities: [
    'Performance monitoring and tuning',
    'Regular maintenance and updates',
    '24/7 technical support',
    'Continuous improvement initiatives'],

    icon: 'ChartBarIcon'
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-6 bg-muted rounded w-1/2 mx-auto" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) =>
                <div key={i} className="h-96 bg-muted rounded-xl" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-brand-primary/5 via-brand-secondary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-primary/10 rounded-full mb-6">
              <Icon name="SparklesIcon" size={20} className="text-brand-primary" />
              <span className="text-sm font-cta font-semibold text-brand-primary">
                Comprehensive IT Solutions
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-primary mb-6">
              Services Built for Your Success
            </h1>
            
            <p className="text-lg md:text-xl font-body text-muted-foreground mb-8">
              From infrastructure management to cybersecurity, we provide the complete technology partnership your business needs to thrive in the digital age.
            </p>

            {/* Search and Filter */}
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border-2 border-border focus:border-brand-primary rounded-lg text-sm font-body text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-200" />

              </div>

              <div className="flex flex-wrap justify-center gap-2">
                {categories.map((category) =>
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-cta font-semibold transition-all duration-200 ${
                  selectedCategory === category ?
                  'bg-brand-primary text-white shadow-card' :
                  'bg-white text-muted-foreground border border-border hover:border-brand-primary'}`
                  }>

                    {category}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service) =>
            <ServiceCard key={service.id} {...service} />
            )}
          </div>

          {filteredServices.length === 0 &&
          <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-headline font-semibold text-primary mb-2">
                No services found
              </h3>
              <p className="text-sm font-body text-muted-foreground">
                Try adjusting your search or filter criteria
              </p>
            </div>
          }
        </div>
      </section>

      {/* Capability Assessment */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <CapabilityAssessment questions={assessmentQuestions} />
          </div>
        </div>
      </section>

      {/* Service Comparison Matrix */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <ServiceComparisonMatrix services={comparisonServices} />
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ImplementationTimeline phases={timelinePhases} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <CTASection />
        </div>
      </section>
    </div>);

}